import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { resourceScopeCleared, userScopeCleared } from '@krp/client-runtime'

import { dropResource, ridKey } from '../../../shared/model/keys'
import type { OperationsIntentState, PendingKind, PendingOperation, PendingPhase } from './types'

const initialState: OperationsIntentState = { pending: {} }

interface OperationRef {
  resourceId: string
  registerKey: string
  operationId: string
}

const keyOf = (ref: { resourceId: string; registerKey: string }) =>
  ridKey(ref.resourceId, ref.registerKey.toLowerCase())

function setPhase(state: OperationsIntentState, ref: OperationRef, phase: PendingPhase): void {
  const entry = state.pending[keyOf(ref)]
  // Ответ на старую команду не трогает новую: сверка по operationId.
  if (entry !== undefined && entry.operationId === ref.operationId) entry.phase = phase
}

/**
 * Команды смены, исход которых клиент ещё ждёт (срок `session`). Это не
 * копия операции сервера: только ЧТО мы отправили (`operationId`) и что знаем
 * о доставке. Статус операции живёт в кэше запроса `aqsiOperation`.
 */
const operationsSlice = createSlice({
  name: 'aqsi/operations',
  initialState,
  reducers: {
    operationStarted(state, action: PayloadAction<OperationRef & { kind: PendingKind; startedAt: string }>) {
      state.pending[keyOf(action.payload)] = { ...action.payload, phase: 'sending' }
    },
    /**
     * Служба отказала 409 `operation-unresolved` и назвала незавершённую команду на кассе (новая вкладка,
     * другое устройство, потерянное хранилище): принять её как свою ждущую — статус и сверка снова доступны.
     */
    operationAdopted(state, action: PayloadAction<OperationRef & { startedAt: string }>) {
      const key = keyOf(action.payload)
      if (state.pending[key]?.operationId === action.payload.operationId) return
      state.pending[key] = { ...action.payload, kind: 'unknown', phase: 'unknown' }
    },
    operationAccepted(state, action: PayloadAction<OperationRef>) {
      setPhase(state, action.payload, 'accepted')
    },
    operationUnknown(state, action: PayloadAction<OperationRef>) {
      setPhase(state, action.payload, 'unknown')
    },
    operationNotFound(state, action: PayloadAction<OperationRef>) {
      setPhase(state, action.payload, 'not-found')
    },
    operationSettled(state, action: PayloadAction<OperationRef>) {
      setPhase(state, action.payload, 'settled')
    },
    /** Служба отказала ДО исполнения (4xx): ждать нечего. */
    operationRejected(state, action: PayloadAction<OperationRef>) {
      const key = keyOf(action.payload)
      if (state.pending[key]?.operationId === action.payload.operationId) delete state.pending[key]
    },
    operationDismissed(state, action: PayloadAction<OperationRef>) {
      const key = keyOf(action.payload)
      if (state.pending[key]?.operationId === action.payload.operationId) delete state.pending[key]
    },
    operationsRestored(state, action: PayloadAction<OperationsIntentState>) {
      state.pending = action.payload.pending
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialState)
    builder.addCase(resourceScopeCleared, (state, action) => dropResource(state.pending, action.payload.resourceId))
  },
})

export const {
  operationStarted,
  operationAdopted,
  operationAccepted,
  operationUnknown,
  operationNotFound,
  operationSettled,
  operationRejected,
  operationDismissed,
  operationsRestored,
} = operationsSlice.actions
export const operationsReducer = operationsSlice.reducer

/** Команда ещё может изменить смену: новую команду смены на этом месте отдавать нельзя. */
export function blocksShiftCommands(pending: PendingOperation | undefined): boolean {
  return pending !== undefined && pending.phase !== 'not-found' && pending.phase !== 'settled'
}

/** Ждущая команда смены ресурса (за кассой ресурса человек один — запись одна; берётся последняя). */
export function pendingOfResource(state: OperationsIntentState, resourceId: string): PendingOperation | undefined {
  const prefix = `${resourceId.toLowerCase()}|`
  let latest: PendingOperation | undefined
  for (const [key, entry] of Object.entries(state.pending)) {
    if (key.startsWith(prefix) && (latest === undefined || entry.startedAt > latest.startedAt)) latest = entry
  }
  return latest
}

/**
 * Ждущая команда, которую показывает окно РМК: на месте моей сессии (её же
 * проверяет запрет повтора), иначе — последняя по ресурсу (сессию уже закрыли или забрали).
 */
export function pendingForWindow(
  state: OperationsIntentState,
  resourceId: string,
  mineRegisterKey: string | null,
): PendingOperation | undefined {
  return (
    (mineRegisterKey !== null ? pendingOfRegister(state, resourceId, mineRegisterKey) : undefined) ??
    pendingOfResource(state, resourceId)
  )
}

export function pendingOfRegister(
  state: OperationsIntentState,
  resourceId: string,
  registerKey: string,
): PendingOperation | undefined {
  return state.pending[keyOf({ resourceId, registerKey })]
}

const PHASES: readonly PendingPhase[] = ['sending', 'accepted', 'unknown', 'not-found', 'settled']
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * Проверка сохранённого (D-072). Команда, которая на момент F5 была «в
 * полёте», читается как `unknown`: ответ на тот POST потерян вместе со
 * страницей, исход проверяется чтением статуса, а не повтором.
 */
export function decodeOperations(raw: unknown): OperationsIntentState | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined
  const pending = (raw as { pending?: unknown }).pending
  if (typeof pending !== 'object' || pending === null) return undefined
  const result: OperationsIntentState = { pending: {} }
  for (const value of Object.values(pending as Record<string, unknown>).slice(0, 50)) {
    if (typeof value !== 'object' || value === null) continue
    const entry = value as Record<string, unknown>
    if (
      typeof entry.resourceId !== 'string' ||
      !UUID.test(entry.resourceId) ||
      typeof entry.registerKey !== 'string' ||
      !UUID.test(entry.registerKey) ||
      typeof entry.operationId !== 'string' ||
      !UUID.test(entry.operationId) ||
      (entry.kind !== 'open' && entry.kind !== 'close' && entry.kind !== 'unknown') ||
      typeof entry.startedAt !== 'string' ||
      !PHASES.includes(entry.phase as PendingPhase)
    ) {
      continue
    }
    const operation: PendingOperation = {
      resourceId: entry.resourceId,
      registerKey: entry.registerKey,
      operationId: entry.operationId,
      kind: entry.kind,
      startedAt: entry.startedAt,
      phase: entry.phase === 'sending' ? 'unknown' : (entry.phase as PendingPhase),
    }
    // Завершённый итог остаётся в журнале сервера, а после F5 не воскресает как текущая неисправность.
    if (operation.phase !== 'settled') result.pending[keyOf(operation)] = operation
  }
  return result
}
